import { Alert, Box, LinearProgress, Stack, Tab, Tabs } from "@mui/material";
import { type FC, type ReactNode, useState } from "react";
import { isEmailChannel } from "../../../modules/channels/types";
import { useMomentPreviewData } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/hooks/use-moment-preview-data";
import { PreviewBody } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/preview-body/preview-body";
import {
  PreviewContextProvider,
  usePreviewContext,
} from "../../../modules/moment/component/buttons/preview-button/preview-dialog/preview-context";
import { PreviewFooter } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/preview-footer";
import { PreviewSidebar } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/preview-sidebar/preview-sidebar";
import type { PreviewMomentData } from "../../../modules/moment/component/buttons/preview-button/preview-dialog/types";
import type { UUID } from "../../../utils/uuid";

type MomentReviewCommonProps = {
  momentId: UUID;
  isNewsletter?: boolean;
  onClose?: () => void;
  buttons?: { sendTest?: ReactNode; publish?: ReactNode };
};

export const MomentReview: FC<
  MomentReviewCommonProps & {
    data?: PreviewMomentData;
  }
> = ({ data, ...props }) => {
  if (data) return <LoadedMomentReview data={data} {...props} />;
  return <MomentReviewLoader {...props} />;
};

const MomentReviewLoader: FC<MomentReviewCommonProps> = (props) => {
  const { data, loading, error } = useMomentPreviewData(props.momentId);

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error.message}</Alert>;
  if (!data) return null;

  return <LoadedMomentReview data={data} {...props} />;
};

const LoadedMomentReview: FC<
  MomentReviewCommonProps & {
    data: PreviewMomentData;
  }
> = ({ data, ...props }) => {
  return (
    <PreviewContextProvider data={data}>
      <MomentReviewInner {...props} />
    </PreviewContextProvider>
  );
};

const MomentReviewInner: FC<MomentReviewCommonProps> = ({
  momentId,
  isNewsletter,
  onClose,
  buttons,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const {
    previewMode,
    setPreviewMode,
    moment_preview_data: { channel },
  } = usePreviewContext();

  return (
    <Box>
      {isEmailChannel(channel) && (
        <Stack direction="row" justifyContent="flex-end">
          <Tabs
            value={previewMode}
            onChange={(_, value) => setPreviewMode(value as "desktop" | "mobile")}
          >
            <Tab value="desktop" label="Desktop" />
            <Tab value="mobile" label="Mobile" />
          </Tabs>
        </Stack>
      )}
      <Box sx={{ display: "flex", height: "calc(100vh - 200px)" }}>
        <PreviewSidebar open={sidebarOpen} isReview />
        <PreviewBody momentId={momentId} isNewsletter={isNewsletter} />
      </Box>
      <PreviewFooter
        sidebarOpen={sidebarOpen}
        toggleSidebar={() => setSidebarOpen((open) => !open)}
        onClose={onClose}
        momentId={momentId}
        isReview
        closePreviewDialog={onClose}
        buttons={buttons}
      />
    </Box>
  );
};
